import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Trash2, Library, Copy, Code2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { SearchInput } from '@/components/shared/SearchInput';
import { TYPE_COLORS, DATA_TYPE_OPTIONS, FORMAT_OPTIONS } from '@/lib/admin/field-utils';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import type { Database, Json } from '@/integrations/supabase/types';

type FieldDataType = Database['public']['Enums']['field_data_type'];

interface FieldLibraryItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  data_type: FieldDataType;
  format: string | null;
  pattern: string | null;
  min_length: number | null;
  max_length: number | null;
  minimum: number | null;
  maximum: number | null;
  is_common: boolean;
  display_order: number | null;
  example_value: string | null;
  // ADF fields
  adf_tag: string | null;
  adf_attributes: Json | null;
  adf_notes: string | null;
}

interface FormData {
  slug: string;
  name: string;
  description: string;
  data_type: FieldDataType;
  format: string;
  pattern: string;
  min_length: number | null;
  max_length: number | null;
  minimum: number | null;
  maximum: number | null;
  is_common: boolean;
  display_order: number;
  example_value: string;
  // ADF fields
  adf_tag: string;
  adf_attributes: string; // JSON string for editing
  adf_notes: string;
}

const initialFormData: FormData = {
  slug: '',
  name: '',
  description: '',
  data_type: 'string',
  format: '',
  pattern: '',
  min_length: null,
  max_length: null,
  minimum: null,
  maximum: null,
  is_common: true,
  display_order: 0,
  example_value: '',
  adf_tag: '',
  adf_attributes: '',
  adf_notes: '',
};

export function FieldLibraryManager() {
  const { toast } = useToast();

  // Search & filter state (kept separate)
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showCommonOnly, setShowCommonOnly] = useState(false);
  const [showAdfOnly, setShowAdfOnly] = useState(false);

  // For usage counts (custom logic)
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>({});

  const {
    items: rawFields,
    loading,
    editorOpen,
    setEditorOpen,
    editingItem: editingField,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete: fieldToDelete,
    isDeleting,
    fetchData,
    openCreateEditor,
    openEditEditor,
    handleSave,
    confirmDelete,
    handleDelete,
  } = useAdminCrud<FieldLibraryItem, FormData>({
    table: 'field_library',
    select: '*',
    orderBy: { column: 'display_order' },
    entityName: 'field',
    getInitialFormData: () => initialFormData,
    mapItemToForm: (field) => ({
      slug: field.slug,
      name: field.name,
      description: field.description || '',
      data_type: field.data_type,
      format: field.format || '',
      pattern: field.pattern || '',
      min_length: field.min_length,
      max_length: field.max_length,
      minimum: field.minimum,
      maximum: field.maximum,
      is_common: field.is_common,
      display_order: field.display_order || 0,
      example_value: field.example_value || '',
      adf_tag: field.adf_tag || '',
      adf_attributes: field.adf_attributes ? JSON.stringify(field.adf_attributes, null, 2) : '',
      adf_notes: field.adf_notes || '',
    }),
    buildPayload: (form) => {
      // Parse ADF attributes if provided
      let parsedAdfAttributes = null;
      if (form.adf_attributes.trim()) {
        try {
          parsedAdfAttributes = JSON.parse(form.adf_attributes);
        } catch {
          // Validation will catch this
        }
      }
      return {
        slug: form.slug || form.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        name: form.name,
        description: form.description || null,
        data_type: form.data_type,
        format: form.format || null,
        pattern: form.pattern || null,
        min_length: form.min_length,
        max_length: form.max_length,
        minimum: form.minimum,
        maximum: form.maximum,
        is_common: form.is_common,
        display_order: form.display_order,
        example_value: form.example_value || null,
        adf_tag: form.adf_tag || null,
        adf_attributes: parsedAdfAttributes,
        adf_notes: form.adf_notes || null,
      };
    },
    validatePayload: (form) => {
      if (!form.name) return 'Name is required';
      if (form.adf_attributes.trim()) {
        try {
          JSON.parse(form.adf_attributes);
        } catch {
          return 'ADF Attributes must be valid JSON';
        }
      }
      return null;
    },
  });

  // Fetch usage counts separately (custom logic)
  useEffect(() => {
    const fetchUsageCounts = async () => {
      const { data: mappings } = await supabase
        .from('standard_field_mappings')
        .select('field_id');

      const counts: Record<string, number> = {};
      mappings?.forEach((m) => {
        counts[m.field_id] = (counts[m.field_id] || 0) + 1;
      });
      setUsageCounts(counts);
    };
    fetchUsageCounts();
  }, [rawFields]);

  // Merge usage counts with fields
  const fields = useMemo(() => {
    return rawFields.map((f) => ({
      ...f,
      usage_count: usageCounts[f.id] || 0,
    }));
  }, [rawFields, usageCounts]);

  // Filtered fields
  const filteredFields = useMemo(() => {
    return fields.filter((field) => {
      const matchesSearch =
        !searchQuery ||
        field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || field.data_type === typeFilter;
      const matchesCommon = !showCommonOnly || field.is_common;
      const matchesAdf = !showAdfOnly || !!field.adf_tag;
      return matchesSearch && matchesType && matchesCommon && matchesAdf;
    });
  }, [fields, searchQuery, typeFilter, showCommonOnly, showAdfOnly]);

  // Custom duplicate action
  const handleDuplicate = async (field: FieldLibraryItem & { usage_count: number }, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSlug = `copy_of_${field.slug}`;
    const { error } = await supabase.from('field_library').insert([{
      slug: newSlug,
      name: `Copy of ${field.name}`,
      description: field.description,
      data_type: field.data_type,
      format: field.format,
      pattern: field.pattern,
      min_length: field.min_length,
      max_length: field.max_length,
      minimum: field.minimum,
      maximum: field.maximum,
      is_common: field.is_common,
      display_order: (field.display_order || 0) + 1,
      example_value: field.example_value,
      adf_tag: field.adf_tag,
      adf_attributes: field.adf_attributes,
      adf_notes: field.adf_notes,
    }]);

    if (error) {
      toast({ title: 'Error duplicating field', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Field duplicated' });
      fetchData();
    }
  };

  const getFormatOptions = (type: FieldDataType) => {
    return FORMAT_OPTIONS[type] || [];
  };

  const formatConstraints = (field: FieldLibraryItem & { usage_count: number }) => {
    const parts: string[] = [];

    if (field.data_type === 'string') {
      if (field.min_length !== null) parts.push(`min: ${field.min_length}`);
      if (field.max_length !== null) parts.push(`max: ${field.max_length}`);
    } else if (field.data_type === 'number' || field.data_type === 'integer') {
      if (field.minimum !== null) parts.push(`≥ ${field.minimum}`);
      if (field.maximum !== null) parts.push(`≤ ${field.maximum}`);
    }

    return parts.length > 0 ? parts.join(', ') : '—';
  };

  // Find usage count for delete warning
  const deleteFieldUsageCount = fieldToDelete ? usageCounts[fieldToDelete.id] || 0 : 0;
  const deleteWarning =
    deleteFieldUsageCount > 0
      ? `This field is used in ${deleteFieldUsageCount} standard(s). Deleting it will remove it from all standards.`
      : undefined;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Field Library"
        count={fields.length}
        onAdd={openCreateEditor}
        addLabel="Add Field"
      />

      {/* Filter Bar */}
      <div className="flex flex-wrap gap-3">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search fields..."
          className="flex-1 min-w-[200px]"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DATA_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch id="common-filter" checked={showCommonOnly} onCheckedChange={setShowCommonOnly} />
          <Label htmlFor="common-filter" className="text-sm cursor-pointer">
            Common only
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="adf-filter" checked={showAdfOnly} onCheckedChange={setShowAdfOnly} />
          <Label htmlFor="adf-filter" className="text-sm cursor-pointer">
            ADF fields only
          </Label>
        </div>
      </div>

      {loading ? (
        <AdminTableSkeleton columns={7} rows={5} />
      ) : filteredFields.length === 0 ? (
        <AdminEmptyState
          icon={Library}
          title={fields.length === 0 ? 'No fields yet' : 'No matching fields'}
          description={
            fields.length === 0
              ? 'Create reusable field definitions for your data standards.'
              : 'Try adjusting your search or filters.'
          }
          actionLabel={fields.length === 0 ? 'Add Field' : undefined}
          onAction={fields.length === 0 ? openCreateEditor : undefined}
        />
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-28">Format</TableHead>
                <TableHead className="w-32">Constraints</TableHead>
                <TableHead className="w-20">Common</TableHead>
                <TableHead className="w-24">ADF Tag</TableHead>
                <TableHead className="w-24">Usage</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFields.map((field) => (
                <TableRow
                  key={field.id}
                  className="cursor-pointer"
                  onClick={() => openEditEditor(field)}
                >
                  <TableCell>
                    <Badge className={TYPE_COLORS[field.data_type] || ''} variant="secondary">
                      {field.data_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{field.name}</div>
                    {field.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {field.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-xs">
                      {field.slug}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{field.format || '—'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{formatConstraints(field)}</span>
                  </TableCell>
                  <TableCell>
                    {field.is_common ? (
                      <Badge variant="default" className="text-xs">
                        Yes
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {field.adf_tag ? (
                      <Badge variant="outline" className="font-mono text-xs">
                        &lt;{field.adf_tag}&gt;
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {field.usage_count > 0 ? (
                      <Badge variant="secondary" className="text-xs">
                        {field.usage_count} {field.usage_count === 1 ? 'standard' : 'standards'}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">Unused</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div
                      className="flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditEditor(field)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDuplicate(field, e)}
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => confirmDelete(field)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingField ? 'Edit Field' : 'Add Field'}
        description={
          editingField
            ? 'Update the field definition.'
            : 'Create a reusable field for data standards.'
        }
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingField ? 'Update' : 'Create'}
      >
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Info</h3>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., First Name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                placeholder="auto-generated if empty (e.g., first_name)"
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this field..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_common">Common Field</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    id="is_common"
                    checked={formData.is_common}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_common: checked })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {formData.is_common ? 'Core field' : 'Standard-specific'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Type & Format */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Type & Format</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_type">Data Type *</Label>
                <Select
                  value={formData.data_type}
                  onValueChange={(value: FieldDataType) =>
                    setFormData({ ...formData, data_type: value, format: '' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATA_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="format">Format</Label>
                <Select
                  value={formData.format || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, format: value === 'none' ? '' : value })}
                  disabled={getFormatOptions(formData.data_type).length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFormatOptions(formData.data_type).map((opt) => (
                      <SelectItem key={opt.value || 'none'} value={opt.value || 'none'}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.data_type === 'string' && (
              <div className="space-y-2">
                <Label htmlFor="pattern">Regex Pattern</Label>
                <Input
                  id="pattern"
                  value={formData.pattern}
                  onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                  placeholder="e.g., ^[A-Z][a-z]+$"
                  className="font-mono"
                />
              </div>
            )}

            {/* Example Value */}
            <div className="space-y-2">
              <Label htmlFor="example_value">Example Value</Label>
              <Textarea
                id="example_value"
                value={formData.example_value}
                onChange={(e) => setFormData({ ...formData, example_value: e.target.value })}
                placeholder={
                  formData.data_type === 'object' || formData.data_type === 'array'
                    ? '{"key": "value"} or ["item1", "item2"]'
                    : 'e.g., John, 2024, true'
                }
                rows={formData.data_type === 'object' || formData.data_type === 'array' ? 4 : 2}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Realistic example used in API documentation. For objects/arrays, use valid JSON.
              </p>
            </div>
          </div>

          {/* Constraints */}
          {(formData.data_type === 'string' ||
            formData.data_type === 'number' ||
            formData.data_type === 'integer') && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Constraints</h3>

              {formData.data_type === 'string' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min_length">Min Length</Label>
                    <Input
                      id="min_length"
                      type="number"
                      value={formData.min_length ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          min_length: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      placeholder="No minimum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_length">Max Length</Label>
                    <Input
                      id="max_length"
                      type="number"
                      value={formData.max_length ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_length: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      placeholder="No maximum"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minimum">Minimum</Label>
                    <Input
                      id="minimum"
                      type="number"
                      value={formData.minimum ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          minimum: e.target.value ? parseFloat(e.target.value) : null,
                        })
                      }
                      placeholder="No minimum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maximum">Maximum</Label>
                    <Input
                      id="maximum"
                      type="number"
                      value={formData.maximum ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          maximum: e.target.value ? parseFloat(e.target.value) : null,
                        })
                      }
                      placeholder="No maximum"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ADF Mapping Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Code2 className="h-4 w-4" />
              ADF Mapping
            </h3>
            <p className="text-xs text-muted-foreground">
              Configure how this field maps to ADF XML elements.
            </p>

            <div className="space-y-2">
              <Label htmlFor="adf_tag">ADF Tag Name</Label>
              <Input
                id="adf_tag"
                value={formData.adf_tag}
                onChange={(e) => setFormData({ ...formData, adf_tag: e.target.value })}
                placeholder="e.g., name, email, phone"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                The XML tag name used in ADF output (without angle brackets).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adf_attributes">ADF Attributes (JSON)</Label>
              <Textarea
                id="adf_attributes"
                value={formData.adf_attributes}
                onChange={(e) => setFormData({ ...formData, adf_attributes: e.target.value })}
                placeholder='{"part": "first", "type": "work"}'
                className="font-mono text-sm"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Optional XML attributes as JSON object.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adf_notes">ADF Notes</Label>
              <Textarea
                id="adf_notes"
                value={formData.adf_notes}
                onChange={(e) => setFormData({ ...formData, adf_notes: e.target.value })}
                placeholder="Implementation notes for ADF mapping..."
                rows={2}
              />
            </div>
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={fieldToDelete?.name}
        itemType="field"
        isDeleting={isDeleting}
        warning={deleteWarning}
      />
    </div>
  );
}
